/**
 * Keycloak login for the Intermal SPA — authorization code flow with PKCE.
 *
 * Why this file exists: the backend runs with OIDC_ENABLED=true in every
 * Comm deployment (kubernetes/intermal/configmap.yaml), which mounts
 * oidcMiddleware in front of ALL of /api — including /api/auth/login. The
 * local e-mail/password form therefore cannot authenticate against a
 * deployed backend; the browser has to obtain a Keycloak access token
 * itself and send it as `Authorization: Bearer`.
 *
 * Deliberately dependency-free (Web Crypto + fetch) so no new package
 * lands in the shared pnpm lockfile. Same pattern as
 * ausschreibungsmatcher/client/src/services/authService.ts.
 *
 * Tokens live in memory only (services/tokenStore.ts), never in Web
 * Storage. After a reload trySilentLogin() fetches fresh ones from the
 * Keycloak session. sessionStorage only holds the one-time PKCE verifier,
 * state and return path of a running login.
 */
import { runtimeEnv, isOidcConfigured } from '../types/runtimeEnv';
import {
  clearSession,
  getAccessToken as tokenAusSpeicher,
  getOidcRefreshToken,
  setAccessToken,
  setOidcRefreshToken,
} from './tokenStore';

const VERIFIER_KEY = 'intermal.pkce.verifier';
const STATE_KEY = 'intermal.pkce.state';
const RETURN_KEY = 'intermal.pkce.returnTo';
const REDIRECT_KEY = 'intermal.pkce.redirectUri';

export const CALLBACK_PATH = '/oidc/callback';
/** Static page that relays a silent (prompt=none) response to the SPA. */
export const SILENT_PATH = '/silent-renew.html';

export { isOidcConfigured };

function base64Url(bytes: Uint8Array): string {
  let str = '';
  for (let i = 0; i < bytes.byteLength; i += 1) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randString(len: number): string {
  const arr = new Uint8Array(len);
  window.crypto.getRandomValues(arr);
  return base64Url(arr);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64Url(new Uint8Array(digest));
}

function issuer(): string {
  return runtimeEnv().OIDC_ISSUER_URL.replace(/\/$/, '');
}

function redirectUri(): string {
  return runtimeEnv().OIDC_REDIRECT_URI || `${window.location.origin}${CALLBACK_PATH}`;
}

export function getAccessToken(): string | null {
  return tokenAusSpeicher();
}

export function storeTokens(accessToken: string, refreshToken?: string): void {
  setAccessToken(accessToken);
  if (refreshToken) setOidcRefreshToken(refreshToken);
}

export function clearTokens(): void {
  clearSession();
}

/**
 * Build the Keycloak authorization URL and persist the PKCE verifier +
 * state for the callback. Split out from startLogin() so it can be tested
 * without jsdom navigation.
 */
export async function buildAuthorizeUrl(
  returnTo?: string,
  opts?: { prompt?: string; redirectUri?: string },
): Promise<string> {
  const env = runtimeEnv();
  if (!isOidcConfigured()) {
    throw new Error('SSO ist nicht konfiguriert (env.js ohne OIDC_ISSUER_URL/OIDC_CLIENT_ID).');
  }

  const verifier = randString(32);
  const challenge = await sha256Challenge(verifier);
  const state = randString(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(
    RETURN_KEY,
    returnTo ?? `${window.location.pathname}${window.location.search}`,
  );

  const redirect = opts?.redirectUri ?? redirectUri();
  sessionStorage.setItem(REDIRECT_KEY, redirect);

  const url = new URL(`${issuer()}/protocol/openid-connect/auth`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', env.OIDC_CLIENT_ID);
  url.searchParams.set('redirect_uri', redirect);
  url.searchParams.set('scope', 'openid profile email');
  url.searchParams.set('state', state);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  // prompt=none asks Keycloak to answer from an existing realm session
  // without ever rendering a login page.
  if (opts?.prompt) url.searchParams.set('prompt', opts.prompt);
  return url.toString();
}

/**
 * Redirect the browser to Keycloak. `returnTo` is remembered so the user
 * lands back on the page that triggered the login.
 */
export async function startLogin(returnTo?: string): Promise<void> {
  const url = await buildAuthorizeUrl(returnTo);
  if (isFramed()) {
    // Keycloak refuses to render its login page inside a cross-origin frame
    // (X-Frame-Options), so take the whole tab there instead. Browsers allow
    // a framed document to navigate top when it acts on a user gesture.
    try {
      if (window.top) {
        window.top.location.href = url;
        return;
      }
    } catch {
      // Top navigation blocked — fall through to a new tab.
    }
    window.open(url, '_blank', 'noopener');
    return;
  }
  window.location.assign(url);
}

/**
 * Pick up an existing Keycloak session without any user interaction:
 * prompt=none never renders a login page, so it survives being loaded in a
 * hidden iframe, and SILENT_PATH relays the result back by postMessage.
 *
 * This is what makes the Comm WebUI tile open straight into the app for a
 * user who is already signed in to comm.sectepe.com.
 */
export async function trySilentLogin(timeoutMs = 8000): Promise<boolean> {
  if (!isOidcConfigured()) return false;

  const url = await buildAuthorizeUrl(
    `${window.location.pathname}${window.location.search}`,
    { prompt: 'none', redirectUri: `${window.location.origin}${SILENT_PATH}` },
  );

  const search = await new Promise<string | null>((resolve) => {
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.title = 'SSO';
    let settled = false;

    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      frame.remove();
      resolve(value);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'intermal-oidc-silent') return;
      finish(String(event.data.search ?? ''));
    };

    const timer = setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('message', onMessage);
    frame.src = url;
    document.body.appendChild(frame);
  });

  if (search === null) return false;
  try {
    await completeLogin(new URLSearchParams(search));
    return true;
  } catch {
    // login_required / interaction_required: the user has to click.
    return false;
  }
}

/**
 * Exchange the authorization code for tokens. Returns the path the user
 * came from so the caller can navigate back there.
 */
export async function completeLogin(params: URLSearchParams): Promise<string> {
  const env = runtimeEnv();
  const error = params.get('error');
  if (error) {
    throw new Error(params.get('error_description') || `Keycloak meldete: ${error}`);
  }

  const code = params.get('code');
  const state = params.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!code || !state || !verifier || state !== expectedState) {
    throw new Error('Ungültiger SSO-Rücksprung (state oder code_verifier fehlt).');
  }

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: env.OIDC_CLIENT_ID,
    code,
    code_verifier: verifier,
    // Must be byte-identical to the authorization request's redirect_uri,
    // which is the silent-renew page for a prompt=none round-trip.
    redirect_uri: sessionStorage.getItem(REDIRECT_KEY) || redirectUri(),
  });
  const resp = await fetch(`${issuer()}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!resp.ok) {
    throw new Error(`Token-Austausch fehlgeschlagen (HTTP ${resp.status}).`);
  }
  const json = await resp.json();
  storeTokens(json.access_token, json.refresh_token);

  const returnTo = sessionStorage.getItem(RETURN_KEY) || '/';
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  sessionStorage.removeItem(REDIRECT_KEY);
  // Never bounce back onto the callback route itself.
  return returnTo.startsWith(CALLBACK_PATH) ? '/' : returnTo;
}

/**
 * Refresh the Keycloak access token. Used by the axios 401 interceptor in
 * place of /api/auth/refresh, which only knows the legacy opaque tokens.
 */
export async function refreshAccessToken(): Promise<string> {
  const env = runtimeEnv();
  const refreshToken = getOidcRefreshToken();
  if (!refreshToken) throw new Error('Kein Refresh-Token vorhanden.');

  const resp = await fetch(`${issuer()}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: env.OIDC_CLIENT_ID,
      refresh_token: refreshToken,
    }),
  });
  if (!resp.ok) throw new Error(`Refresh fehlgeschlagen (HTTP ${resp.status}).`);
  const json = await resp.json();
  storeTokens(json.access_token, json.refresh_token);
  return json.access_token as string;
}

export function buildLogoutUrl(): string {
  const env = runtimeEnv();
  const url = new URL(`${issuer()}/protocol/openid-connect/logout`);
  url.searchParams.set('client_id', env.OIDC_CLIENT_ID);
  url.searchParams.set('post_logout_redirect_uri', window.location.origin);
  return url.toString();
}

/** RP-initiated logout: drop local tokens, then end the Keycloak session. */
export function logout(): void {
  const url = buildLogoutUrl();
  clearTokens();
  window.location.assign(url);
}

/**
 * Inside the Comm WebUI iframe a top-level redirect to Keycloak would be
 * refused by its X-Frame-Options, so the login page shows a button that
 * the user clicks instead of redirecting automatically.
 */
export function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}
